import { useState, useEffect, useCallback, useRef } from 'react';
import { getStudyFormsWithFields } from '@/services/studyFormsApi';
import type { StudyFormWithFields } from '@/types/studyForms';

interface UseStudyFormsOptions {
  enabled?: boolean;
  autoLoad?: boolean;
}

interface UseStudyFormsResult {
  forms: StudyFormWithFields[];
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
  hasForms: boolean;
}

// Global cache to share loaded forms across components
let globalFormsCache: StudyFormWithFields[] | null = null;
let globalLoadingPromise: Promise<StudyFormWithFields[]> | null = null;
let globalError: Error | null = null;

/**
 * Hook for loading study forms with caching and error handling
 * Provides centralized loading for all data-import pages
 */
export function useStudyForms(
  options: UseStudyFormsOptions = {}
): UseStudyFormsResult {
  const { enabled = true, autoLoad = true } = options;
  const [forms, setForms] = useState<StudyFormWithFields[]>(
    globalFormsCache || []
  );
  // Initialize loading state: true if there's an ongoing request but no cache yet
  const [loading, setLoading] = useState<boolean>(
    !globalFormsCache && !!globalLoadingPromise
  );
  const [error, setError] = useState<Error | null>(globalError);
  const isMountedRef = useRef(true);

  const loadForms = useCallback(async () => {
    // If already cached, use cache immediately
    if (globalFormsCache) {
      setForms(globalFormsCache);
      setError(null);
      setLoading(false);
      return;
    }

    // If already loading globally, wait for that promise
    if (globalLoadingPromise) {
      // Set loading state while waiting for existing promise
      setLoading(true);
      setError(null);
      try {
        const cachedForms = await globalLoadingPromise;
        if (isMountedRef.current) {
          setForms(cachedForms);
          setError(null);
          setLoading(false);
        }
        return;
      } catch (err) {
        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error('Failed to load forms'));
          setLoading(false);
        }
        return;
      }
    }

    // Start loading - no cache and no ongoing request
    setLoading(true);
    setError(null);

    const loadPromise = (async () => {
      try {
        const response = await getStudyFormsWithFields({ per_page: 10000 });
        const loadedForms = response.data;

        // Update global cache
        globalFormsCache = loadedForms;
        globalError = null;

        if (isMountedRef.current) {
          setForms(loadedForms);
          setError(null);
        }

        return loadedForms;
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error('Failed to load study forms');
        globalError = errorObj;

        if (isMountedRef.current) {
          setError(errorObj);
        }

        throw errorObj;
      } finally {
        globalLoadingPromise = null;
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    })();

    globalLoadingPromise = loadPromise;

    try {
      await loadPromise;
    } catch {
      // Error already handled above
    }
  }, []);

  const reload = useCallback(async () => {
    // Clear cache to force reload
    globalFormsCache = null;
    globalLoadingPromise = null;
    globalError = null;
    await loadForms();
  }, [loadForms]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (enabled && autoLoad) {
      loadForms();
    }
  }, [enabled, autoLoad, loadForms]);

  return {
    forms,
    loading,
    error,
    reload,
    hasForms: forms.length > 0,
  };
}

/**
 * Clear global cache (useful when study changes)
 */
export function clearStudyFormsCache(): void {
  globalFormsCache = null;
  globalLoadingPromise = null;
  globalError = null;
}

